import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import CancelIcon from '@mui/icons-material/Cancel';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Button, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  DocumentIngestion_ExtractFieldsResult,
  DocumentIngestionType,
  EstateType,
  useDocumentIngestion_ExtractFieldsMutation,
} from 'queries';

import FormInputErrorMessage from 'common/form-input-error-message';
import FormSelectField from 'common/form-select-field';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  content: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
  },
  title: {
    fontWeight: 700,
    fontSize: '24px',
    marginTop: '18px',
    marginBottom: '32px',
    alignSelf: 'center',
  },
  inputBox: {
    border: '1px solid #ECEEF1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '106px',
    borderRadius: '8px',
    flexDirection: 'column',
    color: '#596674',
    padding: '27px 0',
  },
  uploadTitle: {
    fontSize: '14px',
    color: '#596674',
    '& span': {
      color: '#5ED1F0',
      cursor: 'pointer',
    },
  },
  selectContainer: {
    marginTop: '32px',
  },
  selectInput: {
    borderRadius: '8px',
    '& fieldset': {
      border: '1px solid #ECEEF1',
    },
  },
  uploadButton: {
    borderRadius: '100px',
    maxHeight: '40px',
    marginTop: '40px',
    marginBottom: '20px',
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
  },
  fileTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  error: {
    border: '1px solid red',
  },
  spinner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& span': {
      fontSize: '14px',
    },
  },
}));

type EstateDetailDocumentIngestionModalProps = {
  portalId: string;
  onClose: () => void;
  onExtractFields: (extractedFields: DocumentIngestion_ExtractFieldsResult) => void;
  setShowModal: (showModal: boolean) => void;
  estateType: EstateType;
  showModal: boolean;
};

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type FormUploadValuesType = {
  files?: File[];
  documentIngestionType: DocumentIngestionType;
};

export const EstateDetailDocumentIngestionModal = ({
  portalId,
  onClose,
  onExtractFields,
  setShowModal,
  showModal,
  estateType,
}: EstateDetailDocumentIngestionModalProps) => {
  const classes = useStyles();

  const [executeExtractDataFromFile] = useDocumentIngestion_ExtractFieldsMutation({
    onCompleted: (extractFieldsData) => onExtractFields(extractFieldsData.data),
    onError: (e) => {
      showAlert({
        message: 'Error with document ingestion: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const documentIngestionTypeOptions = useMemo(() => {
    switch (estateType) {
      case EstateType.Truadmin:
      case EstateType.Trustadmin:
        return [{ label: 'Death Certificate', value: DocumentIngestionType.DeathCertificate }];
      case EstateType.Truplanning:
      case EstateType.Truproxy:
        return [{ label: 'Drivers License', value: DocumentIngestionType.DriversLicense }];
    }
  }, [estateType]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    setError,
    reset,
  } = useForm<FormUploadValuesType>({
    defaultValues: {
      documentIngestionType: documentIngestionTypeOptions[0]?.value,
    },
  });

  const onSubmit = async (values: FormUploadValuesType) => {
    const { files, documentIngestionType } = values;
    if (!files || files.length === 0) {
      setError('files', { message: 'Required' });
      return;
    }
    const file = files[0];
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('files', { message: `File size should not exceed ${MAX_FILE_SIZE_MB} MB` });
      return;
    }

    await executeExtractDataFromFile({
      variables: {
        input: {
          file,
          documentIngestionType,
          portalId,
        },
      },
    });
    reset();
  };

  const files = watch('files');
  const fileName = files?.length ? files[0].name : '';
  const filesErrorMessage = errors.files?.message;

  const renderUploadContainer = () => {
    return (
      <label
        htmlFor="file_input"
        className={clsx(classes.inputBox, {
          [classes.error]: filesErrorMessage,
        })}
      >
        <input id="file_input" {...register('files', { required: true })} type="file" hidden />
        <FileUploadIcon />

        <Typography className={classes.uploadTitle}>
          <span>Click to upload a file</span>
        </Typography>
      </label>
    );
  };

  const renderFileTitle = () => {
    if (isSubmitting) {
      return (
        <div className={classes.spinner}>
          <Spinner />
          <span>Uploading your document</span>
        </div>
      );
    }

    return (
      <div className={classes.inputBox}>
        <div className={classes.fileTitle}>
          <Typography>{fileName}</Typography>
          <IconButton
            aria-label="delete"
            color="primary"
            onClick={() => setValue('files', undefined)}
          >
            <CancelIcon />
          </IconButton>
        </div>
      </div>
    );
  };

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={onClose}
      showModal={showModal}
      showActions={false}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.content}>
            <Typography className={classes.title}>Upload</Typography>
            {fileName ? renderFileTitle() : renderUploadContainer()}
            {filesErrorMessage && <FormInputErrorMessage error={filesErrorMessage} />}

            <div className={classes.selectContainer}>
              <Controller
                name="documentIngestionType"
                control={control}
                render={({ field: { ref: _, ...field } }) => (
                  <FormSelectField
                    {...field}
                    errorMessage={errors.documentIngestionType?.message}
                    options={documentIngestionTypeOptions}
                    label="Document Type"
                    className={classes.selectInput}
                  />
                )}
              />
            </div>

            <Button
              type="button"
              variant="outlined"
              className={classes.uploadButton}
              disabled={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              Upload
            </Button>
          </div>
        </form>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};
